/* eslint-disable @typescript-eslint/explicit-function-return-type */
import Store from '@/main/store';
import Router from '@/main/router';

import { IWindow } from '@/interfaces/Window';
import { ICheckout, ICheckoutCartOffer } from '@/interfaces/Checkout';
import { IOrder } from '@/interfaces/Order';
import { IOrderDatalayer } from '@/interfaces/Datalayer';
import { ITraceableError } from '@/interfaces/Error';
import { IUpsell } from '@/interfaces/Upsell';

import {
  EEligibilityAction,
  EAuthenticationType,
  EAuthenticationOrigin,
  EAuthenticationAction,
  EAuthenticationTokenStatus,
  EPaymentMethod
} from './enums';

export function pushToDataLayer(data: Object): void {
  if ((window as unknown as IWindow).dataLayer) {
    (window as unknown as IWindow).dataLayer.push(data);
  }
}

export const generatingPaymentStructure = (paymentStructures: any): any => {
  let additionalPaymentConditions: any = [];

  paymentStructures.forEach((el: any) => {
    additionalPaymentConditions.push({
      ...generateAdditionalPaymentConditions(el),
    });
  });

  return additionalPaymentConditions;
};

export const generateAdditionalPaymentConditions = (paymentStructure: any): any => {
  let additionalPaymentCondition: any = {};
  let paymentMethodCode = paymentStructure.payment_conditions.reduce((a: any, b: any) => a.payment_method_code + '-' + b.payment_method_code);

  if (paymentStructure) {
    additionalPaymentCondition = {
      ...paymentStructure,
      payment_method_code: paymentMethodCode,
    };
  }

  return additionalPaymentCondition;
};

export const paymentConditionsMultiPayment = (paymentStructureFiltered: any, offerSelected: any, methodSelected?: any): any => {
  let paymentStructure = null;

  if (methodSelected) {
    paymentStructure = generatingPaymentStructure(paymentStructureFiltered);

    const paymentConditionSelected = paymentStructure.find((el: any) => {
      return el.payment_method_code === methodSelected
        && el.payment_conditions.find((value: any) => value.payment_method_code === EPaymentMethod.creditCard);
    });

    return paymentConditionSelected;
  }

  paymentStructure = paymentStructureFiltered.find((el: any) => {
    return el.payment_conditions && el.payment_conditions[0].payment_method_code === offerSelected.payment.selectedPaymentMethod;
  });

  return paymentStructure.payment_conditions[0];
};

export const paymentStructureByOfferSelected = (offerSelected: any): any => {
  const paymentStructure: any = offerSelected && offerSelected.payment_structure && offerSelected.payment_structure;
  const multiPaymentActived = Store.state.App.Checkout.creditCard.filterActived;
  const paymentStructureMultiPayment = paymentStructure.filter((el: any) => !!el.is_multi_payment_options && el.payment_conditions.length > 1);
  const paymentStructureNotMultiPayment = paymentStructure.filter((el: any) => !el.is_multi_payment_options);

  if (multiPaymentActived && ['creditcard-creditcard', 'creditcard-pix'].includes(multiPaymentActived))
    return paymentConditionsMultiPayment(paymentStructureMultiPayment, offerSelected, multiPaymentActived);

  return paymentConditionsMultiPayment(paymentStructureNotMultiPayment, offerSelected);
};

export const buildOrderDatalayer = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer): any => {
  const multiPaymentActived = Store.state.App.Checkout.creditCard.filterActived;
  const isMultiPayment = ['creditcard-creditcard', 'creditcard-pix'].includes(multiPaymentActived);
  const rootFinalPrice = paymentStructureByOfferSelected(offerRoot);
  const selectedFinalPrice = paymentStructureByOfferSelected(offerSelected);

  return {
    id: order.id,
    kind: offerRoot.eligibility ? offerRoot.eligibility.eligible_action : EEligibilityAction.purchase,
    sales_channel: order && order.source ? order.source : '',
    offers: {
      root: {
        id: offerRoot.id,
        slug: offerRoot.slug,
        price: offerRoot.price,
        final_price: isMultiPayment ? rootFinalPrice.cart_subtotal_price : rootFinalPrice.cart_final_price,
        payment_method: offerRoot.payment.selectedPaymentMethod,
      },
      selected: {
        id: offerSelected.id,
        slug: offerSelected.slug,
        price: offerSelected.price,
        final_price: isMultiPayment ? selectedFinalPrice.cart_subtotal_price : selectedFinalPrice.cart_final_price,
        payment_method: offerSelected.payment.selectedPaymentMethod,
      },
    },
  };
};

export const buildOrderDatalayerOld = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer): IOrderDatalayer => {
  return {
    id: order.id,
    kind: offerRoot.eligibility ? offerRoot.eligibility.eligible_action : EEligibilityAction.purchase,
    sales_channel: order && order.source ? order.source : '',
    offers: {
      id: offerRoot.id,
      slug: Router.currentRoute.params.slug,
      price: offerRoot.price,
    },
    final_price: offerRoot.payment.selectedPaymentMethod.cart_final_price,
  };
};

export const buildCheckoutEverywhereOrderDatalayer = (order: any, offerRoot: any, offerSelected: any): any => {
  return {
    id: order.id,
    kind: offerRoot.eligibility ? offerRoot.eligibility.eligible_action : EEligibilityAction.purchase,
    sales_channel: offerRoot && offerRoot.sales_channel ? offerRoot.sales_channel : '',
    offers: {
      root: {
        id: offerRoot.id,
        slug: offerRoot.slug,
        price: offerRoot.price,
        final_price: offerRoot.final_price,
        payment_method: offerRoot.payment_method,
      },
      selected: {
        id: offerSelected.id,
        slug: offerSelected.slug,
        price: offerSelected.price,
        final_price: offerSelected.final_price,
        payment_method: offerSelected.payment_method,
      },
    },
  };
};

export const pushCheckoutDataToDatalayer = (checkout: ICheckout) => pushCheckoutLoadedToDatalayer(checkout, 'checkout-pgv');

export const pushCheckoutLoadedToDatalayer = (checkout: ICheckout, customName?: string): void => {
  const cart = checkout.cart;
  const offer = cart.offer;
  const eligibility = checkout.cart.offer!.eligibility;

  pushToDataLayer({
    event: customName ? customName : 'checkout-empiricus',
    ecommerce: {
      checkout: {
        kind: eligibility ? eligibility.eligible_action : EEligibilityAction.purchase,
        offers: {
          id: offer ? offer.id : '',
          slug: Router.currentRoute.params.slug,
          price: offer.price ? offer.price : '',
          payment_method: offer.payment.selectedPaymentMethod,
        }
      }
    }
  });
};

export const pushOrderConfirmedToDatalayer = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer): void => {
  pushToDataLayer({
    event: 'pedido-confirmado-related',
    ecommerce: {
      order: buildOrderDatalayer(order, offerRoot, offerSelected),
    }
  });

  pushToDataLayer({
    event: 'pedido-confirmado',
    ecommerce: {
      order: buildOrderDatalayerOld(order, offerRoot, offerSelected),
    }
  });
};

export const purshOrderAwaitingPaymentToDatalayer = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer): void => {
  pushToDataLayer({
    event: 'pedido-aguardando-pagamento',
    ecommerce: {
      order: buildOrderDatalayer(order, offerRoot, offerSelected),
    }
  });
};

export const pushOrderBankslipGeneratedToDatalayer = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer): void => {
  pushToDataLayer({
    event: 'pedido-boleto-gerado-related',
    ecommerce: {
      order: buildOrderDatalayer(order, offerRoot, offerSelected),
    }
  });

  pushToDataLayer({
    event: 'pedido-boleto-gerado',
    ecommerce: {
      order: buildOrderDatalayerOld(order, offerRoot, offerSelected),
    }
  });
};

export const pushOrderUpsellConfirmedToDatalayer = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer, customUpsell = false): void => {
  pushToDataLayer({
    event: 'pedido-confirmado-upsell-related',
    ecommerce: {
      order: {
        ...buildOrderDatalayer(order, offerRoot, offerSelected),
        custom_upsell: customUpsell
      },
    }
  });

  pushToDataLayer({
    event: 'pedido-confirmado-upsell',
    ecommerce: {
      order: buildOrderDatalayerOld(order, offerRoot, offerSelected),
    }
  });
};

export const pushOrderUpsellBankslipGeneratedToDatalayer = (order: IOrder, offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer, customUpsell = false): void => {
  pushToDataLayer({
    event: 'pedido-boleto-gerado-upsell-related',
    ecommerce: {
      order: {
        ...buildOrderDatalayer(order, offerRoot, offerSelected),
        custom_upsell: customUpsell
      }
    }
  });

  pushToDataLayer({
    event: 'pedido-boleto-gerado-upsell',
    ecommerce: {
      order: {
        ...buildOrderDatalayerOld(order, offerRoot, offerSelected),
        custom_upsell: customUpsell
      }
    }
  });
};

export const pushCurrentActaUserIdToDatalayer = (): void => {
  const acta_user_id = Store.state.App.Customer.customer.acta_user_id;

  pushToDataLayer({
    event: 'ActaIdEvent',
    acta_user_id,
  });
};

export const pushSelectedOfferToDatalayer = (offerRoot: ICheckoutCartOffer, offerSelected: ICheckoutCartOffer): void => {
  pushToDataLayer({
    event: 'checkout-selected-offer',
    ecommerce: {
      checkout: {
        offers: {
          root: {
            id: offerRoot.id,
            slug: offerRoot.slug,
          },
          selected: {
            id: offerSelected.id,
            slug: offerSelected.slug,
          },
        },
      },
    },
  });
};

export const pushPaymentMethodSelectedToDatalayer = (offerSelected: ICheckoutCartOffer, method: EPaymentMethod): void => {
  pushToDataLayer({
    event: 'checkout-selected-payment-method',
    ecommerce: {
      checkout: {
        paymentMethod: method,
      },
    },
  });
};

interface IEligibilityBlock {
  code: string | number;
  slug: string | number;
  acta_user_id?: string | number;
}

export const pushEligibilityBlockToDatalayer = (eligibilityBlock: IEligibilityBlock): void => {
  pushToDataLayer({
    event: 'eligibilityBlock',
    ecommerce: {
      eligibility: {
        code: eligibilityBlock.code,
        slug: eligibilityBlock.slug,
        acta_user_id: eligibilityBlock.acta_user_id
      },
    },
  });
};

export interface IRegisterBlock {
  slug: string;
  acta_user_id?: string | number;
}

export const pushRegisterBlockToDatalayer = (registerBlock: IRegisterBlock): void => {
  pushToDataLayer({
    event: 'completeRegistrationExibition',
    slug: registerBlock.slug,
    acta_user_id: registerBlock.acta_user_id,
  });
};

export const pushTraceableErrorToDatalayer = (error: ITraceableError): void => {
  const slug = Router.currentRoute.params.slug || Router.currentRoute.params.contract_id;
  const acta_user_id = Store.state.App.Customer.customer.acta_user_id;

  pushToDataLayer({
    event: 'traceableError',
    slug,
    acta_user_id,
    code: error.code,
    type: error.type,
    block: error.block,
    origin: error.origin,
    trigger: error.trigger,
    severity: error.severity,
    createdAt: error.createdAt,
    exhibition: error.exhibition,
  });
};

export interface IAuthenticationAction {
  action: EAuthenticationAction;
  type: EAuthenticationType;
  origin: EAuthenticationOrigin;
  token?: string;
  status?: EAuthenticationTokenStatus;
}

export const pushAuthenticationActionToDatalayer = (action: IAuthenticationAction): void => {
  const eventSession = getAuthEventOnSession(action.action);

  const event = {
    event: action.action,
    type: action.type,
    origin: action.origin,
    status: action.status,
    token: action.token || Store.state.App.Customer.token.value,
    dateTime: new Date().toUTCString(),
  };

  const deleteNotLogoutAuthEvents = (): void => {
    deleteAuthEventOnSession(EAuthenticationAction.login);
    deleteAuthEventOnSession(EAuthenticationAction.loginAs);
    deleteAuthEventOnSession(EAuthenticationAction.register);
  };

  if (!eventSession) {
    pushToDataLayer(event);
    setAuthEventOnSession(action);

    if (action.action === EAuthenticationAction.logout) {
      deleteNotLogoutAuthEvents();
    }
  } else if (
    action.action === EAuthenticationAction.logout
  ) {
    if (
      action.type === EAuthenticationType.automatic &&
      eventSession !== action
    ) {
      deleteAuthEventOnSession(EAuthenticationAction.logout);
    } else if (
      action.type === EAuthenticationType.manual
    ) {
      pushToDataLayer(event);
      deleteNotLogoutAuthEvents();
    }
  }
};

export const getAuthEventOnSession = (eventAction: EAuthenticationAction): any => {
  const session = sessionStorage.getItem(eventAction) as string;

  return session && JSON.parse(session);
};

export const setAuthEventOnSession = (event: IAuthenticationAction): any => {
  return sessionStorage.setItem(event.action, JSON.stringify(event));
};

export const deleteAuthEventOnSession = (eventAction: EAuthenticationAction): any => {
  return sessionStorage.removeItem(eventAction);
};

export const pushRefusalUpsellClickToDatalayer = (order: IOrder, upsell: IUpsell, refusalLink?: string): void => {
  pushToDataLayer({
    event: 'refusal-upsell-click',
    ecommerce: {
      order: order.id,
      acta_user_id: order.customer!.id,
      upsell: upsell.id,
      upsell_offer: upsell.offer_id,
      refusal_type: upsell.refusal_upsell ? 'pgrp' : 'final',
      refusal_link: refusalLink || null,
      refusal_upsell: upsell.refusal_upsell ? upsell.refusal_upsell.id : null,
    },
  });
};

export const pushRefusalUpsellLoadedToDatalayer = (order: IOrder, upsell: IUpsell): void => {
  pushToDataLayer({
    event: 'refusal-upsell-loaded',
    ecommerce: {
      order: order.id,
      acta_user_id: order.customer!.id,
      upsell: upsell.id,
      upsell_offer: upsell.offer_id,
    },
  });
};

export const pushCheckoutEverywhereOpenToDataLayer = (): void => {
  pushToDataLayer({
    event: 'abertura-ck-everywhere',
  });
};

export const pushCheckoutEverywhereOrderConfirmedToDatalayer = (order: any, offerRoot: any, offerSelected: any): void => {
  pushToDataLayer({
    event: 'pedido-confirmado-related',
    ecommerce: {
      order: buildCheckoutEverywhereOrderDatalayer(order, offerRoot, offerSelected),
    }
  });
};
